import { useState } from 'react';
import {
  Card, CardContent, Grid, Stack, Button, TextField, Typography, Box, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, IconButton, Tooltip,
} from '@mui/material';
import { Add, Factory, Inventory2, AccountBalanceWallet, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ListFilters, matchFilters, EMPTY_FILTERS, type FilterValue } from '../../components/ListFilters';
import { sourcingApi } from '../../api/sourcing.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
type Form = { name: string; contactPerson: string; email: string; mobile: string; gstNumber: string; panNumber: string; bankName: string; bankAccount: string; bankIfsc: string; paymentCreditDays: number; commercialTerms: string };
const blank: Form = { name: '', contactPerson: '', email: '', mobile: '', gstNumber: '', panNumber: '', bankName: '', bankAccount: '', bankIfsc: '', paymentCreditDays: 45, commercialTerms: '' };

export function ManufacturersPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });
  const [form, setForm] = useState<Form>(blank);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dash = useQuery({ queryKey: ['mfr', 'dashboard'], queryFn: () => sourcingApi.dashboard() });
  const list = useQuery({ queryKey: ['mfr', 'list'], queryFn: () => sourcingApi.listManufacturers({ limit: 200 }) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['mfr'] }); };

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const onboard = useMutation({
    mutationFn: () => sourcingApi.onboard({ ...form, email: form.email || undefined, paymentCreditDays: Number(form.paymentCreditDays) }),
    onSuccess: () => { setOpen(false); setForm(blank); setError(null); setToast('Manufacturer onboarded'); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const del = useMutation({
    mutationFn: (id: number) => sourcingApi.removeManufacturer(id),
    onSuccess: () => { setToast('Manufacturer deleted'); invalidate(); },
    onError: (e) => setToast(extractApiError(e)),
  });

  const d = dash.data;
  return (
    <>
      <PageHeader title="Manufacturers" subtitle="Manufacturer master, performance, pending deliveries & stock status."
        action={hasPermission('manufacturer.create') ? <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>Onboard Manufacturer</Button> : undefined} />

      <ListFilters value={filters} onChange={setFilters} statusOptions={['active', 'inactive']} show={['search', 'status']} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Metric icon={<Factory />} label="Manufacturers" value={`${d?.manufacturers.total ?? 0} (${d?.manufacturers.active ?? 0} active)`} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<Inventory2 />} label="Pending deliveries" value={d?.orders.pendingDeliveries ?? 0} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<Inventory2 />} label="Stock pending (qty)" value={d?.stock.pendingQty ?? 0} /></Grid>
        <Grid item xs={6} md={3}><Metric icon={<AccountBalanceWallet />} label="Outstanding payable" value={inr(d?.outstandingPayable)} /></Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>GST</TableCell><TableCell align="right">Credit Days</TableCell><TableCell>Status</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {(list.data?.data ?? []).filter((m) => matchFilters(filters, { text: `${m.name} ${m.manufacturerCode} ${m.gstNumber ?? ''}`, status: m.status })).map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>{m.manufacturerCode}</TableCell>
                  <TableCell><Typography fontWeight={600}>{m.name}</Typography>{m.contactPerson && <Typography variant="caption" color="text.secondary">{m.contactPerson}</Typography>}</TableCell>
                  <TableCell>{m.gstNumber ?? '—'}</TableCell>
                  <TableCell align="right">{m.paymentCreditDays}</TableCell>
                  <TableCell><Chip size="small" color={m.status === 'active' ? 'success' : 'default'} label={m.status} /></TableCell>
                  <TableCell align="right">{hasPermission('manufacturer.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => del.mutate(m.id)}><Delete fontSize="small" /></IconButton></Tooltip>}</TableCell>
                </TableRow>
              ))}
              {(list.data?.data.length ?? 0) === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" py={2} textAlign="center">No manufacturers yet.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Onboard Manufacturer</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack spacing={1.5} mt={1}>
            <TextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} fullWidth required />
            <Stack direction="row" spacing={1}>
              <TextField label="Contact Person" value={form.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} fullWidth />
              <TextField label="Mobile" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} fullWidth />
            </Stack>
            <TextField label="Email" value={form.email} onChange={(e) => set('email', e.target.value)} fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label="GST Number" value={form.gstNumber} onChange={(e) => set('gstNumber', e.target.value)} fullWidth />
              <TextField label="PAN" value={form.panNumber} onChange={(e) => set('panNumber', e.target.value)} fullWidth />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField label="Bank Name" value={form.bankName} onChange={(e) => set('bankName', e.target.value)} fullWidth />
              <TextField label="Account No." value={form.bankAccount} onChange={(e) => set('bankAccount', e.target.value)} fullWidth />
              <TextField label="IFSC" value={form.bankIfsc} onChange={(e) => set('bankIfsc', e.target.value)} fullWidth />
            </Stack>
            <TextField label="Payment Credit Days" type="number" value={form.paymentCreditDays} onChange={(e) => set('paymentCreditDays', Number(e.target.value))} helperText="Standard 45 days from invoice received date" />
            <TextField label="Commercial Terms" value={form.commercialTerms} onChange={(e) => set('commercialTerms', e.target.value)} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.name || onboard.isPending} onClick={() => onboard.mutate()}>Onboard</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', color: '#fff', borderRadius: 2, boxShadow: '0 6px 18px rgba(30,58,138,0.18)' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}><Typography variant="h6" fontWeight={800} lineHeight={1.15} noWrap>{value}</Typography><Typography variant="caption" sx={{ opacity: 0.85 }}>{label}</Typography></Box>
      </CardContent>
    </Card>
  );
}
